import React from 'react';
import styled from '@emotion/styled';
import { MapType, SettlementSelectedType } from '../../types/dataTypes';
import { BREAKPOINTS, COLORS_BASE, STYLES } from '../../utils/consts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClose, faDownload, faMagnifyingGlassPlus,
  faLocationDot, faMapPin, faClipboardList, faQuoteLeft,
  faBuilding, faRoad, faTag,
} from '@fortawesome/free-solid-svg-icons';
import { CloseButton } from '../../styles/styles';
import Button from '../Shared/Button';
import { downloadSettlementProfile, downloadSettlementProfileAsCSV } from '../../utils/settlementProfiles';

type SetttlementPopupProps = {
  selectedSettlement: SettlementSelectedType;
  onClose: () => void;
  map: MapType;
};

function toSentenceCase(value: string) {
  const normalized = value.trim().replace(/\s+/g, ' ');

  if (!normalized) return '';

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function uniqueValues(values: string[]) {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

function getProfileFacilities(featuredProfile: NonNullable<SettlementSelectedType['featuredProfile']>) {
  const fallbackProfile = featuredProfile as unknown as {
    social_facilities?: string[];
  };

  return uniqueValues([
    ...featuredProfile.public_and_economic_profile.public_facilities,
    ...(fallbackProfile.social_facilities ?? []),
  ]);
}

function hasFacilityMatch(facilities: string[], pattern: RegExp) {
  return facilities.some((facility) => pattern.test(facility.toLowerCase()));
}

function getYesNoLabel(value: boolean) {
  return value ? 'YES' : 'NO';
}

function getActivitySummary(featuredProfile: NonNullable<SettlementSelectedType['featuredProfile']>) {
  const fallbackProfile = featuredProfile as unknown as {
    commercial_activities?: string[];
    productive_uses?: string[];
  };

  const combinedActivities = uniqueValues([
    ...featuredProfile.public_and_economic_profile.main_livelihood_activities,
    ...featuredProfile.public_and_economic_profile.main_productive_use_activities,
    ...featuredProfile.public_and_economic_profile.main_commercial_activities,
    ...(fallbackProfile.productive_uses ?? []),
    ...(fallbackProfile.commercial_activities ?? []),
  ]).map(toSentenceCase);

  return combinedActivities.length > 0 ? combinedActivities.join(', ') : 'Not reported';
}

function getTransportSummary(featuredProfile: NonNullable<SettlementSelectedType['featuredProfile']>) {
  const transportProfile = featuredProfile as unknown as {
    transport?: string[] | string | null;
    transport_options?: string[] | string | null;
    transport_available?: string[] | string | null;
    available_transport?: string[] | string | null;
    transport_modes?: string[] | string | null;
    means_of_transport?: string[] | string | null;
    public_and_economic_profile?: {
      transport?: string[] | string | null;
      transport_options?: string[] | string | null;
      transport_available?: string[] | string | null;
      available_transport?: string[] | string | null;
    };
    access_and_readiness_summary?: {
      transport?: string[] | string | null;
      transport_options?: string[] | string | null;
      transport_available?: string[] | string | null;
      available_transport?: string[] | string | null;
    };
  };

  const transportCandidates = [
    transportProfile.transport,
    transportProfile.transport_options,
    transportProfile.transport_available,
    transportProfile.available_transport,
    transportProfile.transport_modes,
    transportProfile.means_of_transport,
    transportProfile.public_and_economic_profile?.transport,
    transportProfile.public_and_economic_profile?.transport_options,
    transportProfile.public_and_economic_profile?.transport_available,
    transportProfile.public_and_economic_profile?.available_transport,
    transportProfile.access_and_readiness_summary?.transport,
    transportProfile.access_and_readiness_summary?.transport_options,
    transportProfile.access_and_readiness_summary?.transport_available,
    transportProfile.access_and_readiness_summary?.available_transport,
  ];

  const transportValues = uniqueValues(
    transportCandidates.flatMap((candidate) => {
      if (!candidate) return [];
      return Array.isArray(candidate) ? candidate : [candidate];
    })
  ).map(toSentenceCase);

  return transportValues.length > 0 ? transportValues.join(', ') : 'Not reported';
}

function SetttlementPopup({ selectedSettlement, onClose, map }: SetttlementPopupProps) {
  const featuredProfile = selectedSettlement.featuredProfile;
  const nesLabel = featuredProfile?.nes_planning_classification.display_label
    ?? featuredProfile?.nes_planning_classification.nes_tag;
  const facilities = featuredProfile ? getProfileFacilities(featuredProfile) : [];
  const hasSchool = hasFacilityMatch(facilities, /(primary|secondary)\s+school|schools?/);
  const hasClinic = hasFacilityMatch(facilities, /clinic|health\s*center|health centre/);
  const activitySummary = featuredProfile ? getActivitySummary(featuredProfile) : '';
  const transportSummary = featuredProfile ? getTransportSummary(featuredProfile) : '';

  return (
    <CardContainer>
      {/* ── Header ── */}
      <PopupHeader isSurvey={!!featuredProfile}>
        <HeaderIcon>
          <FontAwesomeIcon icon={featuredProfile ? faClipboardList : faMapPin} />
        </HeaderIcon>
        <HeaderTitle>{selectedSettlement.name}</HeaderTitle>
        {featuredProfile && <SurveyBadge>Pilot village Survey</SurveyBadge>}
        <CloseButton onClick={onClose} style={{ position: 'absolute', top: 10, right: 10, color: 'white' }}>
          <FontAwesomeIcon icon={faClose} />
        </CloseButton>
      </PopupHeader>

      <PopupBody>
        {/* ── Location section ── */}
        <SectionLabel>
          <FontAwesomeIcon icon={faLocationDot} />
          Location
        </SectionLabel>
        <FieldTable>
          {featuredProfile ? (
            <>
              <FieldRow>
                <FieldIcon><FontAwesomeIcon icon={faBuilding} /></FieldIcon>
                <FieldKey>District</FieldKey>
                <FieldVal>{featuredProfile.settlement_identity.district}</FieldVal>
              </FieldRow>
              <FieldRow>
                <FieldIcon><FontAwesomeIcon icon={faBuilding} /></FieldIcon>
                <FieldKey>Council</FieldKey>
                <FieldVal>{featuredProfile.settlement_identity.council}</FieldVal>
              </FieldRow>
            </>
          ) : null}
          <FieldRow>
            <FieldIcon><FontAwesomeIcon icon={faMapPin} /></FieldIcon>
            <FieldKey>Coordinates</FieldKey>
            <FieldVal>
              {Number(selectedSettlement.coordinates[1]).toFixed(4)},&nbsp;
              {Number(selectedSettlement.coordinates[0]).toFixed(4)}
            </FieldVal>
          </FieldRow>
        </FieldTable>

        {/* ── Survey section (featured only) ── */}
        {featuredProfile && (
          <>
            <Divider />
            <SectionLabel>
              <FontAwesomeIcon icon={faClipboardList} />
              Survey details
            </SectionLabel>
            <FieldTable>
              <FieldRow>
                <FieldIcon><FontAwesomeIcon icon={faClipboardList} /></FieldIcon>
                <FieldKey>Survey type</FieldKey>
                <FieldVal>{featuredProfile.basic_scale.template_type ?? 'Village baseline'}</FieldVal>
              </FieldRow>
              <FieldRow>
                <FieldIcon><FontAwesomeIcon icon={faBuilding} /></FieldIcon>
                <FieldKey>Primary or secondary school</FieldKey>
                <FieldVal>{getYesNoLabel(hasSchool)}</FieldVal>
              </FieldRow>
              <FieldRow>
                <FieldIcon><FontAwesomeIcon icon={faBuilding} /></FieldIcon>
                <FieldKey>Clinic available</FieldKey>
                <FieldVal>{getYesNoLabel(hasClinic)}</FieldVal>
              </FieldRow>
              <FieldRow>
                <FieldIcon><FontAwesomeIcon icon={faTag} /></FieldIcon>
                <FieldKey>Commercial activities</FieldKey>
                <FieldVal>{activitySummary}</FieldVal>
              </FieldRow>
              <FieldRow>
                <FieldIcon><FontAwesomeIcon icon={faRoad} /></FieldIcon>
                <FieldKey>Transport</FieldKey>
                <FieldVal>{transportSummary}</FieldVal>
              </FieldRow>
              {nesLabel && (
                <FieldRow>
                  <FieldIcon><FontAwesomeIcon icon={faTag} /></FieldIcon>
                  <FieldKey>NES tag</FieldKey>
                  <FieldVal><NesTag>{nesLabel}</NesTag></FieldVal>
                </FieldRow>
              )}
              {featuredProfile.access_and_readiness_summary.readiness_notes.length > 0 && (
                <FieldRow>
                  <FieldIcon><FontAwesomeIcon icon={faClipboardList} /></FieldIcon>
                  <FieldKey>Readiness note</FieldKey>
                  <FieldVal>{featuredProfile.access_and_readiness_summary.readiness_notes[0]}</FieldVal>
                </FieldRow>
              )}
            </FieldTable>
          </>
        )}

        {/* ── Non-featured details ── */}
        {!featuredProfile && (
          <>
            <Divider />
            <SectionLabel>
              <FontAwesomeIcon icon={faClipboardList} />
              Settlement data
            </SectionLabel>
            <FieldTable>
              <FieldRow>
                <FieldIcon><FontAwesomeIcon icon={faBuilding} /></FieldIcon>
                <FieldKey>Rooftop count</FieldKey>
                <FieldVal>{selectedSettlement.building_c}</FieldVal>
              </FieldRow>
              <FieldRow>
                <FieldIcon><FontAwesomeIcon icon={faRoad} /></FieldIcon>
                <FieldKey>Distance to grid</FieldKey>
                <FieldVal>{selectedSettlement.grid_d_km} km</FieldVal>
              </FieldRow>
              <FieldRow>
                <FieldIcon><FontAwesomeIcon icon={faBuilding} /></FieldIcon>
                <FieldKey>Building density</FieldKey>
                <FieldVal>{selectedSettlement.build_d_pe}%</FieldVal>
              </FieldRow>
            </FieldTable>
          </>
        )}

        {/* ── Storyline (featured only) ── */}
        {featuredProfile && (featuredProfile.storytelling_fields.short_description || featuredProfile.storytelling_fields.why_this_village_matters) && (
          <>
            <Divider />
            <SectionLabel>
              <FontAwesomeIcon icon={faQuoteLeft} />
              Settlement Charaterization
            </SectionLabel>
            <StoryText>
              {featuredProfile.storytelling_fields.short_description
                ?? featuredProfile.storytelling_fields.why_this_village_matters}
            </StoryText>
          </>
        )}

        {/* ── Default storyline fallback ── */}
        {featuredProfile && !featuredProfile.storytelling_fields.short_description && !featuredProfile.storytelling_fields.why_this_village_matters && (
          <>
            <Divider />
            <StoryText muted>
              This site is part of the initial village survey showcase. Detailed narrative will be added in upcoming DOE releases.
            </StoryText>
          </>
        )}
      </PopupBody>

      {/* ── Actions ── */}
      <ActionRow>
        <Button icon={faMagnifyingGlassPlus} onClick={() => {
          if (map.current) map.current.fitBounds(selectedSettlement.bbox as any);
        }}>
          Fly to
        </Button>
        {featuredProfile && (
          <Button icon={faDownload} onClick={() => downloadSettlementProfileAsCSV(featuredProfile)}>
            Download record
          </Button>
        )}
      </ActionRow>
    </CardContainer>
  );
}

/* ── Styled components ── */

const CardContainer = styled.div`
  position: fixed;
  left: ${STYLES.SIDEBAR_WIDTH + 40}px;
  bottom: 60px;
  width: 320px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
  z-index: 1000;
  overflow: hidden;
  color: ${COLORS_BASE.BRAND.DARKER};
  font-size: 13px;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    left: 12px;
    right: 12px;
    bottom: 92px;
    width: auto;
    max-width: calc(100vw - 24px);
  }
`;

const PopupHeader = styled.div<{ isSurvey: boolean }>(({ isSurvey }) => `
  position: relative;
  background-color: ${isSurvey ? '#F59E0B' : COLORS_BASE.BRAND.MAIN};
  color: white;
  padding: 14px 48px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`);

const HeaderIcon = styled.span`
  font-size: 11px;
  opacity: 0.85;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
`;

const SurveyBadge = styled.span`
  display: inline-block;
  margin-top: 4px;
  padding: 2px 8px;
  background: rgba(255,255,255,0.25);
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  width: fit-content;
`;

const PopupBody = styled.div`
  padding: 12px 14px 4px 14px;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${COLORS_BASE.GREYS.DARKER};
  margin: 10px 0 6px 0;

  svg {
    color: #F59E0B;
    font-size: 12px;
  }
`;

const FieldTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 16px 110px 1fr;
  align-items: baseline;
  gap: 6px;
`;

const FieldIcon = styled.span`
  color: ${COLORS_BASE.BRAND.MAIN};
  font-size: 11px;
  text-align: center;
`;

const FieldKey = styled.span`
  color: ${COLORS_BASE.GREYS.DARKER};
  font-weight: 600;
`;

const FieldVal = styled.span`
  color: ${COLORS_BASE.BRAND.DARKER};
  word-break: break-word;
`;

const NesTag = styled.span`
  display: inline-block;
  padding: 1px 8px;
  border-radius: 12px;
  background: ${COLORS_BASE.GREEN.MAIN};
  color: ${COLORS_BASE.BRAND.DARKER};
  font-size: 11px;
  font-weight: 600;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${COLORS_BASE.GREYS.HIGHLIGHT};
  margin: 10px 0 0 0;
`;

const StoryText = styled.p<{ muted?: boolean }>(({ muted }) => `
  margin: 6px 0 0 0;
  line-height: 1.5;
  color: ${muted ? COLORS_BASE.GREYS.DARKER : COLORS_BASE.BRAND.DARKER};
  font-style: ${muted ? 'italic' : 'normal'};
`);

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 14px 14px 14px;
  border-top: 1px solid ${COLORS_BASE.GREYS.HIGHLIGHT};
  margin-top: 10px;

  button {
    flex: 1;
    justify-content: center;
    margin: 0;
    border: none;
    font-size: 12px;
    font-weight: 600;
  }
`;

export default SetttlementPopup;